import { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { ServerCrash, Cpu, Network, Tag, Trash2, AlertTriangle, CheckCircle2, ChevronDown, Radio, RefreshCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import AdoptModal from './AdoptModal'
import { useConfirm } from './ConfirmDialog'
import VmcResetDialog, { getResetMode } from './VmcReset'
import { fmtRelative } from '../i18n/format'
import { apiError } from '../lib/api'

const IFACE_CLS = {
  up: 'bg-status-op/15 text-status-op',
  down: 'bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500',
}
const ifaceTxt = (t, state) =>
  state === 'up' ? t('ops:devices.iface_up')
  : state === 'down' ? t('ops:devices.iface_down')
  : (state || '—')

async function rejectPending(id) {
  const res = await fetch(`/api/pending-devices/${id}`, { method: 'DELETE' })
  if (!res.ok) throw await apiError(res)
}

// Card verde (provisionado, aguardando as primeiras mensagens): o device já
// existe de verdade (creds, broker, IoT), então o delete precisa ser o
// coordenado completo, que envia o comando de reset ao equipamento.
async function deleteProvisioned(name) {
  const res = await fetch(`/api/devices/${encodeURIComponent(name)}?device=true`, { method: 'DELETE' })
  if (!res.ok) throw await apiError(res)
  return res.json().catch(() => null)
}

async function retryBootstrap(id) {
  const res = await fetch(`/api/pending-devices/${id}/retry`, { method: 'POST' })
  if (!res.ok) throw await apiError(res)
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-3 py-1 border-b border-slate-100 dark:border-slate-800 last:border-0">
      <span className="text-slate-400 dark:text-slate-500 shrink-0">{label}</span>
      <span className="text-slate-700 dark:text-slate-200 text-right truncate">{value || '—'}</span>
    </div>
  )
}

export default function PendingDeviceCard({ pending }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [warning, setWarning] = useState(null)
  const [resetOpen, setResetOpen] = useState(false)
  const [confirmDialog, ask] = useConfirm()
  const { can } = useAuth()
  // Adotar, retentar e rejeitar exigem engineer no backend.
  const mayAct = can('engineer')
  const info = pending.device_info || {}
  const sys  = info.system || {}
  const net  = info.network || {}
  const ifaces = Array.isArray(net.interfaces) ? net.interfaces : []
  const board = [sys.board_vendor, sys.board_name].filter(Boolean).join(' ')
  const os = [sys.os_id, sys.os_version].filter(Boolean).join(' ')
  const primaryIface = ifaces.find(i => i.state === 'up') || ifaces[0]

  const createdStr = fmtRelative(pending.created_at)

  const isAdopted = pending.status === 'adopted'
  const isFailed = pending.status === 'failed'
  const isRetry = pending.status === 'retry'
  const isProvisioned = pending.status === 'provisioned'
  // O delete do card verde é o DELETE /api/devices/{name}?device=true, que é de admin.
  const mayRemove = isProvisioned ? can('admin') : mayAct
  const err = pending.error || {}
  const stepLabel = err.step ? t(`ops:devices.steps.${err.step}`, { defaultValue: err.step }) : null

  const border = isFailed ? 'border-red-300'
    : isProvisioned ? 'border-status-op/60'
    : isAdopted ? 'border-blue-300'
    : 'border-yellow-400'
  const ring = expanded ? 'ring-2 ring-brand/25' : ''
  const Icon = isProvisioned ? CheckCircle2 : ServerCrash
  const iconColor = isFailed ? 'text-red-500'
    : isProvisioned ? 'text-status-op'
    : 'text-yellow-500'
  // O delete do card verde é o expurgo coordenado (reset do VMC + IoT + broker + VPN +
  // bootstrap + banco), o mesmo que em outro lugar exige caixa de confirmação: aqui pede
  // o vmc-id digitado. Com o serviço de reset ligado, pede também o motivo e quem limpa é
  // ele (VmcReset.jsx). Rejeitar um pending é reversível e pede só confirmação simples.
  const askDelete = async () => {
    setWarning(null)
    if (isProvisioned) {
      if (await getResetMode()) {
        setResetOpen(true)
        return
      }
      ask({
        title: t('ops:devices.actions.device_title'),
        message: t('ops:devices.delete_provisioned_confirm', { name: pending.vmc_name }),
        confirmWord: pending.vmc_name,
        confirmLabel: t('common:actions.delete'),
        danger: true,
        onConfirm: async () => {
          const body = await deleteProvisioned(pending.vmc_name)
          if (body?.warnings?.length) setWarning(t('ops:devices.actions.warn_reset_not_delivered'))
        },
      })
      return
    }
    ask({
      title: t('ops:devices.reject'),
      message: t('ops:devices.reject_confirm', { name: pending.vmc_name }),
      confirmLabel: t('ops:devices.reject'),
      danger: true,
      onConfirm: () => rejectPending(pending.id),
    })
  }

  const askRetry = () => ask({
    title: t('ops:devices.retry_bootstrap'),
    message: t('ops:devices.retry_confirm', { name: pending.vmc_name }),
    confirmLabel: t('ops:devices.retry_bootstrap'),
    onConfirm: () => retryBootstrap(pending.id),
  })

  const badge = isFailed
    ? { cls: 'bg-red-100 text-red-700', txt: t('ops:devices.badge_failed') }
    : isRetry
      ? { cls: 'bg-amber-100 text-amber-700', txt: t('ops:devices.badge_retry') }
      : isProvisioned
        ? { cls: 'bg-status-op/15 text-status-op', txt: t('ops:devices.badge_provisioned') }
        : isAdopted
          ? { cls: 'bg-blue-100 text-blue-700', txt: t('ops:devices.badge_provisioning') }
          : { cls: 'bg-yellow-100 text-yellow-700', txt: t('ops:devices.badge_waiting_adoption') }

  return (
    <>
      <div className={`bg-white dark:bg-slate-900 rounded-xl border-2 ${border} ${ring} shadow-sm hover:shadow-md transition-all overflow-hidden`}>
        {/* Cabeçalho clicável em div, não em <button>: o botão de apagar vive dentro
            dele e um botão aninhado é HTML inválido (e o teclado ativava o de fora). */}
        <div
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          onClick={() => setExpanded(v => !v)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(v => !v) } }}
          className="w-full text-left cursor-pointer"
        >
          <div className="flex items-start justify-between px-4 pt-4 pb-3">
            <div className="flex items-center gap-2">
              <Icon size={16} className={`${iconColor} shrink-0 mt-0.5`} />
              <div>
                <p className="font-semibold text-slate-800 dark:text-slate-100 text-sm leading-tight">{pending.vmc_name}</p>
                <p className="text-xs text-slate-400 dark:text-slate-500">{t('ops:devices.received', { when: createdStr })}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wide ${badge.cls}`}>
                {badge.txt}
              </span>
              {mayRemove && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); askDelete() }}
                  title={isProvisioned ? t('ops:devices.actions.device_title') : t('ops:devices.reject')}
                  className="text-slate-400 dark:text-slate-500 hover:text-red-500"
                >
                  <Trash2 size={14} />
                </button>
              )}
              <ChevronDown size={15} className={`text-slate-300 dark:text-slate-600 transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`} />
            </div>
          </div>

          <div className="px-4 pb-3 grid grid-cols-1 gap-1 text-xs text-slate-600 dark:text-slate-300">
            <div className="flex items-center gap-1.5">
              <Cpu size={12} className="text-slate-400 dark:text-slate-500 shrink-0" />
              <span className="truncate">{board || '—'}{sys.board_model ? ` · ${sys.board_model}` : ''}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Tag size={12} className="text-slate-400 dark:text-slate-500 shrink-0" />
              <span className="truncate">{os || '—'} · {sys.architecture || '—'}{sys.board_platform_version ? ` · ${sys.board_platform_version}` : ''}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Network size={12} className="text-slate-400 dark:text-slate-500 shrink-0" />
              <span className="truncate">
                {ifaces.length ? t('ops:devices.iface_count', { count: ifaces.length }) : '—'}
                {primaryIface ? ` · ${primaryIface.name} ${primaryIface.mac}` : ''}
              </span>
            </div>
          </div>
        </div>

        {warning && (
          <div className="mx-4 mb-3 rounded-md bg-amber-50 dark:bg-amber-500/10 border border-amber-300 px-3 py-2 text-[11px] text-amber-800 dark:text-amber-300">
            {warning}
          </div>
        )}

        <div className={`grid transition-all duration-300 ease-out ${expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
          <div className="overflow-hidden">
            <div className="mx-4 mb-3 rounded-lg bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800 px-3 py-2.5">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-1">{t('ops:devices.bootstrap_id')}</p>
              <div className="text-xs">
                <Row label={t('ops:devices.fields.vmc_id')} value={pending.vmc_name} />
                <Row label={t('ops:devices.fields.vendor')} value={sys.board_vendor} />
                <Row label={t('ops:devices.fields.board')} value={sys.board_name} />
                <Row label={t('ops:devices.fields.model')} value={sys.board_model} />
                <Row label={t('ops:devices.fields.platform')} value={sys.board_platform_version} />
                <Row label={t('ops:devices.fields.system')} value={os} />
                <Row label={t('ops:devices.fields.architecture')} value={sys.architecture} />
              </div>

              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500 mt-3 mb-1">
                {t('ops:devices.network_ifaces', { count: ifaces.length })}
              </p>
              <div className="space-y-1.5">
                {ifaces.length === 0 && <p className="text-xs text-slate-400 dark:text-slate-500">{t('ops:devices.no_ifaces')}</p>}
                {ifaces.map((i, idx) => {
                  const cls = IFACE_CLS[i.state] || 'bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500'
                  return (
                    <div key={idx} className="flex items-center justify-between gap-2 text-xs">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="font-medium text-slate-700 dark:text-slate-200">{i.name}</span>
                        <span className="text-slate-400 dark:text-slate-500 truncate">{i.mac}</span>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <span className="text-[10px] text-slate-400 dark:text-slate-500 uppercase">{i.type}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${cls}`}>{ifaceTxt(t, i.state)}</span>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>

        {isFailed && (
          <div className="mx-4 mb-3 rounded-md bg-red-50 dark:bg-red-500/10 border border-red-200 px-3 py-2">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-red-700 dark:text-red-300">
              <AlertTriangle size={13} className="shrink-0" />
              <span>{t('ops:devices.failed_at_step', { step: stepLabel || t('ops:devices.unknown_step') })}</span>
            </div>
            {err.message && <p className="mt-1 text-[11px] text-red-600 break-words">{err.message}</p>}
          </div>
        )}

        {isProvisioned ? (
          <div className="px-4 pb-4 pt-2.5 flex items-center gap-2 border-t border-slate-100 dark:border-slate-800">
            <Radio size={13} className="text-status-op animate-pulse shrink-0" />
            <span className="text-xs text-slate-500 dark:text-slate-400">{t('ops:devices.provisioned_waiting')}</span>
          </div>
        ) : (
          <div className="px-4 pb-4 pt-1 flex items-center justify-between border-t border-slate-100 dark:border-slate-800">
            <span className="text-xs text-slate-400 dark:text-slate-500">
              {isFailed ? t('ops:devices.footer_failed')
                : isRetry ? t('ops:devices.footer_retry')
                : isAdopted ? t('ops:devices.footer_adopted')
                : t('ops:devices.footer_waiting')}
            </span>
            {!mayAct ? null : isFailed ? (
              <button
                onClick={askRetry}
                className="text-xs font-semibold px-3 py-1.5 rounded-md bg-amber-500 text-white hover:bg-amber-600 inline-flex items-center gap-1.5"
              >
                <RefreshCw size={13} /> {t('ops:devices.retry_bootstrap')}
              </button>
            ) : (
              <button
                onClick={() => setOpen(true)}
                disabled={isAdopted || isRetry}
                className="text-xs font-semibold px-3 py-1.5 rounded-md bg-brand text-white hover:bg-brand-700 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed"
              >
                {isAdopted || isRetry ? t('ops:devices.wait') : t('ops:devices.adopt')}
              </button>
            )}
          </div>
        )}
      </div>

      {open && (
        <AdoptModal
          pending={pending}
          onClose={() => setOpen(false)}
        />
      )}
      {resetOpen && (
        <VmcResetDialog
          name={pending.vmc_name}
          onClose={() => setResetOpen(false)}
        />
      )}
      {confirmDialog}
    </>
  )
}
